'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  link: string;
  accentColor: string;
}

const ServiceDiscoveryCards = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const services: Service[] = [
    {
      id: 1,
      title: "Custom Web and App Development",
      description: "Full-stack development services using modern frameworks and technologies tailored to your business needs.",
      icon: "CodeBracketIcon",
      features: [
        "React JS, Next JS, Angular JS",
        "Node JS, Python, .Net, Java",
        "Power Apps & Power Automate",
        "SharePoint Development"
      ],
      link: "/services",
      accentColor: "from-primary to-secondary"
    },
    {
      id: 2,
      title: "AI Agents Development",
      description: "Custom intelligent automation solutions that transform business processes and drive measurable outcomes.",
      icon: "CpuChipIcon",
      features: ["Process Automation", "Natural Language Processing", "Predictive Analytics", "Custom AI Models"],
      link: "/services",
      accentColor: "from-primary to-secondary"
    },
    {
      id: 3,
      title: "Contract Staffing",
      description: "Access skilled technology professionals who integrate seamlessly with your team and deliver results.",
      icon: "UsersIcon",
      features: ["Vetted Professionals", "Flexible Engagement", "Quick Onboarding", "Skill Matching"],
      link: "/services",
      accentColor: "from-primary to-secondary"
    },
    {
      id: 4,
      title: "Data Engineering Solutions",
      description: "Build scalable data infrastructure that powers insights and enables data-driven decision making.",
      icon: "CircleStackIcon",
      features: ["Data Pipelines", "Cloud Migration", "ETL Solutions", "Data Warehousing"],
      link: "/services",
      accentColor: "from-primary to-secondary"
    },
    {
      id: 5,
      title: "License Reselling",
      description: "Streamlined access to Microsoft and AWS licenses with expert guidance and competitive pricing.",
      icon: "ShieldCheckIcon",
      features: ["Volume Licensing", "Cost Optimization", "Compliance Support", "Renewal Management"],
      link: "/services",
      accentColor: "from-primary to-secondary"
    },
    {
      id: 6,
      title: "Cloud Solutions",
      description: "End-to-end cloud architecture, migration, and optimization services for Azure and AWS platforms.",
      icon: "CloudIcon",
      features: ["Cloud Architecture", "Migration Services", "Cost Optimization", "Security & Compliance"],
      link: "/services",
      accentColor: "from-primary to-secondary"
    }
  ];

  return (
    <section className="relative bg-background py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Animated background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-primary/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-gradient-to-r from-secondary/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full mb-6 backdrop-blur-sm">
            <Icon name="RocketLaunchIcon" size={20} variant="solid" className="text-primary mr-2" />
            <span className="text-sm font-headline font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Our Solutions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold mb-6">
            <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
              Comprehensive Technology Solutions
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
            From AI-powered automation to skilled talent and cloud infrastructure, we deliver the complete technology ecosystem your business needs.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className="group relative bg-card rounded-2xl border border-primary/30 shadow-xl transition-all duration-300 transform -translate-y-2 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Subtle gradient background - now default */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.accentColor} opacity-5 transition-opacity duration-300`} />
              
              {/* Soft glow effect - now default */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-100 blur-xl transition-opacity duration-300 -z-10" />

              <div className="relative p-6 sm:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className={`relative flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md bg-gradient-to-br ${service.accentColor} scale-105`}>
                    <Icon
                      name={service.icon as any}
                      size={32}
                      variant="outline"
                      className="transition-all duration-300 text-white scale-110"
                    />
                  </div>
                  <Icon
                    name="ArrowTopRightOnSquareIcon"
                    size={20}
                    variant="outline"
                    className="text-primary transition-all duration-300 opacity-100 translate-x-0 -translate-y-0"
                  />
                </div>

                <h3 className="text-xl font-headline font-bold text-primary transition-colors duration-300 break-words mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-muted-foreground font-body mb-6 leading-relaxed break-words">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li 
                      key={idx} 
                      className="flex items-center text-sm text-foreground font-body"
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 transition-all duration-300 bg-gradient-to-br ${service.accentColor}`}>
                        <Icon 
                          name="CheckIcon" 
                          size={12} 
                          variant="solid" 
                          className="text-white" 
                        />
                      </div>
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  href={service.link}
                  className="inline-flex items-center text-sm font-headline font-semibold text-primary hover:text-secondary transition-colors duration-300 group/link"
                >
                  Learn More
                  <Icon name="ArrowRightIcon" size={16} variant="outline" className="ml-2 group-hover/link:translate-x-2 transition-transform duration-300" />
                </Link>
              </div>

              {/* Animated border shimmer - now default */}
              <div className="absolute inset-0 rounded-2xl opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r from-primary/50 via-secondary/50 to-primary/50 bg-clip-border animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <Link
            href="/services"
            className="inline-flex items-center px-8 py-4 bg-conversion text-conversion-foreground font-headline font-semibold text-base rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 relative overflow-hidden group"
          >
            <span className="relative z-10 flex items-center">
              View All Services
              <Icon name="ArrowRightIcon" size={20} variant="outline" className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-conversion/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceDiscoveryCards;